import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Loader2Icon,
  PackageIcon,
  RefreshCwIcon,
  TriangleAlertIcon,
  UploadIcon,
} from "lucide-react";
import { toast } from "sonner";
import type { Address, Hex } from "viem";
import {
  hash16,
  type CapRow,
  type ExtensionManifest,
  type InstallRecord,
  type PackageCheck,
  type PackageProblem,
} from "@woodpecker/protocol";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, PageHeader } from "../ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/overlay";
import { Table, TBody, TD, TH, THead, TR } from "../ui/table";
import { Input, Label, Switch } from "../ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { cn } from "../lib/cn";
import { short } from "../lib/format";
import { useT, Y } from "../i18n";
import { useStore } from "../store/store";
import { useWallet } from "../store/wallet";
import { navigate } from "../components/Shell";
import { capsText, useSignLines } from "../components/signing";
import { useEnsurePayerCap, useIdentitySignIn } from "../components/WalletBlock";
import { Rejected, SignedOut, Waiting } from "../components/common";
import type { ServiceEntry } from "../adapters/chain";
import { HostPackageClient, StoreClient, errorText, type PackageVersion } from "../adapters/clients";
import { sessionOf } from "../adapters/wallet";
import { consoleIdentityOf, rootOf } from "../adapters/identity";
import { loadExtension, loadExtensions, readPackageZip, type ExtensionView } from "../adapters/extensions";
import { loadInstalled, type InstalledView } from "../adapters/installed";
import { paidWrite } from "../adapters/paid-write";

export const manageExtensionHref = (id: string) => `#/extensions/${encodeURIComponent(id)}/manage`;

/** What the manifest declares of the wallet (D77), as short texts. */
function needsOf(t: ReturnType<typeof useT>, m: ExtensionManifest | null): string[] {
  if (!m) return [];
  const out: string[] = [];
  if (m.storage?.config) out.push(t("ext.needs.config", { quota: m.storage.config }));
  if (m.storage?.store) out.push(t("ext.needs.store", { quota: m.storage.store }));
  if (m.services?.length) out.push(t("ext.needs.services", { names: m.services.join(", ") }));
  if (m.permissions?.wallet?.includes("authorize")) out.push(t("ext.needs.wallet"));
  return out.length ? out : [t("ext.needs.none")];
}

function StateBadge({ v }: { v: ExtensionView }) {
  const t = useT();
  const text = !v.verified
    ? v.problem
      ? t("ext.state.unreachable")
      : t("ext.state.unverified")
    : v.active
      ? t("ext.state.active")
      : t("ext.state.unpublished");
  const tone = v.verified && v.active ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400";
  return <span className={cn("text-xs", tone)}>{text}</span>;
}

function ExtensionTable({
  rows,
  empty,
  manage,
  installed,
  onInstall,
}: {
  rows: ExtensionView[];
  empty: string;
  manage: boolean;
  installed: InstallRecord[];
  onInstall: (v: ExtensionView) => void;
}) {
  const t = useT();
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return (
    <Table>
      <THead>
        <TR>
          <TH>{t("ext.col.extension")}</TH>
          <TH>{t("ext.col.version")}</TH>
          <TH>{t("ext.col.hash")}</TH>
          <TH>{t("ext.col.state")}</TH>
          <TH />
        </TR>
      </THead>
      <TBody>
        {rows.map((v) => {
          const has = installed.some((x) => x.id === v.id);
          return (
            <TR key={v.id}>
              <TD>
                <div className="font-medium">{v.manifest?.name ?? v.name}</div>
                <div className="font-mono text-xs text-muted-foreground">{v.id}</div>
                {v.manifest?.description && (
                  <div className="text-xs text-muted-foreground">{v.manifest.description}</div>
                )}
                <div className="text-xs text-muted-foreground">{needsOf(t, v.manifest).join(" · ")}</div>
              </TD>
              <TD className="font-mono text-xs">{v.manifest?.version ?? "—"}</TD>
              <TD className="font-mono text-xs">{v.hash16}</TD>
              <TD>
                <StateBadge v={v} />
              </TD>
              <TD className="text-right">
                <div className="flex flex-wrap justify-end gap-1.5">
                  {has ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/extensions/${encodeURIComponent(v.id)}`)}
                    >
                      <CheckIcon />
                      {t("ext.open")}
                    </Button>
                  ) : (
                    <Button size="sm" disabled={!v.verified || !v.active || !v.manifest} onClick={() => onInstall(v)}>
                      {t("ext.install")}
                    </Button>
                  )}
                  {manage && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/extensions/${encodeURIComponent(v.id)}/manage`)}
                    >
                      {t("ext.manage")}
                    </Button>
                  )}
                </div>
              </TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}

/** `tMemory 1/10 · tCloud 0/5` of an extension's identity (D64). */
export function CapsLine({ rows, className }: { rows: CapRow[]; className?: string }) {
  const t = useT();
  if (rows.length === 0)
    return <span className={cn("text-xs text-muted-foreground", className)}>{t("ext.caps.none")}</span>;
  return (
    <span className={cn("font-mono text-xs", className)}>
      {rows.map((r) => `${r.token} ${r.used}/${r.cap}`).join(" · ")}
    </span>
  );
}

/** The caps of an identity as the API carries them, read from contract A through the store. */
export function useCapsOf() {
  const { refreshAuth } = useStore();
  return useCallback(
    async (id: string, identity: Address): Promise<CapRow[]> => {
      const rows = await refreshAuth(`ext:${id}`, identity);
      return rows.map((r) => ({ token: r.token, cap: r.cap, used: r.used, remaining: r.remaining }));
    },
    [refreshAuth],
  );
}

/**
 * The store's namespace bound to the extension's identity (D74, issue #21): the owner's call at install, over the
 * store session the tab needs anyway (the Console identity's, silently, or the wallet's in the panel). With
 * `ifUnbound`, the tab repeats the call only when the namespace shows another binding or none, so an install whose
 * bind did not go through heals at the first visit. Uninstall unbinds over a session this browser holds; the files
 * stay for the owner.
 */
export function useStoreBinding() {
  const { wallet, config, entry } = useStore();
  const identitySignIn = useIdentitySignIn();
  const t = useT();
  const clientOf = (record: InstallRecord) => {
    const e = record.store ? entry(record.store) : undefined;
    return e && wallet ? new StoreClient(e.origin, wallet.address, config.chainId, e.name) : null;
  };
  const bind = async (record: InstallRecord, opts: { ifUnbound?: boolean } = {}): Promise<boolean> => {
    const client = clientOf(record);
    if (!client || !record.store) return false;
    if (!(await identitySignIn.ensure(record.store))) return false;
    try {
      if (opts.ifUnbound) {
        const ns = await client.list(record.id);
        if (ns.identity && ns.identity.toLowerCase() === record.identity.toLowerCase()) return true;
      }
      await client.bind(record.id, record.identity);
      return true;
    } catch (e) {
      toast.error(t("ext.bindFailed", { name: record.name, error: errorText(e) }));
      return false;
    }
  };
  const unbind = async (record: InstallRecord): Promise<void> => {
    const client = clientOf(record);
    if (!client || !client.session) return;
    try {
      await client.unbind(record.id);
    } catch {
      /* the binding stays with the files: the owner's clear removes both */
    }
  };
  return { bind, unbind };
}

/**
 * Uninstall (D72): one signing-panel request, a multicall of authorize with each cap set to what the extension used,
 * so nothing more settles (D64); then the install record leaves the settings record, one commit by the Console's
 * identity (D75). The configuration namespace stays in the memory service: the person's data, not the extension's.
 * The store's namespace unbinds (issue #21); its files stay too.
 */
export function useUninstall() {
  const { uninstallExtension, settings } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const capsOf = useCapsOf();
  const ensurePayerCap = useEnsurePayerCap();
  const storeBinding = useStoreBinding();
  const t = useT();
  return async (record: InstallRecord, onDone?: () => void) => {
    const rows = await capsOf(record.id, record.identity).catch(() => [] as CapRow[]);
    const used = Object.fromEntries(rows.filter((r) => r.cap > r.used).map((r) => [r.token, r.used]));
    const finish = async () => {
      if (!(await ensurePayerCap([settings.defaultMemory]))) return;
      try {
        await storeBinding.unbind(record);
        await uninstallExtension(record.id);
        toast.success(t("ext.uninstalledToast", { name: record.name }));
        onDone?.();
      } catch (e) {
        toast.error(errorText(e));
      }
    };
    if (Object.keys(used).length === 0) return finish();
    request({
      title: Y("wallet.req.uninstall", { name: record.name }),
      note: Y("wallet.req.uninstallNote", { n: Object.keys(used).length }),
      lines: [
        lines.authorize(
          Y("wallet.plain.uninstall", { name: record.name, caps: capsText(used) }),
          record.identity,
          used,
        ),
      ],
      onDone: (r) => {
        if (r === "signed") void finish();
      },
    });
  };
}

type WalletState = "idle" | "waiting" | "rejected";

/**
 * The install card (D72): what the manifest declares, one cap per token for the extension's identity (the memory's
 * token for its configuration, each service's token), and Install: one request in the signing panel (approve on a
 * token's first use, then one multicall of authorize), then the install record goes into the settings record as one
 * commit by the Console's identity, and the tab opens.
 */
function InstallDialog({ view, onClose }: { view: ExtensionView; onClose: () => void }) {
  const {
    wallet,
    entry,
    settings,
    balances,
    approvedTokens,
    extensionIdentityFor,
    installExtension,
    wpPerToken,
    directory,
    config,
  } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const ensurePayerCap = useEnsurePayerCap();
  const storeBinding = useStoreBinding();
  const t = useT();
  const m = view.manifest!;
  const memoryEntry = entry(settings.defaultMemory);
  const identity = extensionIdentityFor(view.id);
  // one cap per token: the configuration's (the memory's token), then each service's
  const owners = new Map<string, { label: string; entry: ServiceEntry }>();
  if (m.storage?.config && memoryEntry)
    owners.set(memoryEntry.token, {
      label: t("install.payFor.config", { memory: memoryEntry.name }),
      entry: memoryEntry,
    });
  const missing: string[] = [];
  for (const name of m.services ?? []) {
    const e = entry(name);
    if (!e) {
      missing.push(name);
      continue;
    }
    if (!owners.has(e.token)) owners.set(e.token, { label: t("install.payFor.service", { name }), entry: e });
  }
  // the store (D74): one of kind store in the directory, the provider's first; required when the manifest declares one
  const needsStore = !!m.storage?.store;
  const stores = directory
    .filter((e) => e.kinds.includes("store"))
    .sort((a, b) => (a.name.startsWith(`${config.provider}/`) ? -1 : b.name.startsWith(`${config.provider}/`) ? 1 : 0));
  const [storeName, setStoreName] = useState<string>(stores[0]?.name ?? "");
  const storeEntry = needsStore ? stores.find((s) => s.name === storeName) : undefined;
  if (storeEntry && !owners.has(storeEntry.token))
    owners.set(storeEntry.token, { label: t("install.payFor.store", { name: storeEntry.name }), entry: storeEntry });
  const tokensNeeded = [...owners.keys()];
  const [caps, setCaps] = useState<Record<string, number>>({});
  const [state, setState] = useState<WalletState>("idle");
  const busy = state === "waiting";
  const capOf = (token: string) => caps[token] ?? Math.max(1, owners.get(token)?.entry.defaultCap ?? 20);
  const balanceOf = (token: string) => balances[token] ?? 0;
  const above = (token: string) => capOf(token) > balanceOf(token);
  const allCaps = Object.fromEntries(tokensNeeded.map((tok) => [tok, capOf(tok)]));
  const approveNeeded = tokensNeeded.filter((tok) => !approvedTokens.has(tok));
  const totalWp = tokensNeeded.reduce((s, tok) => s + capOf(tok) * wpPerToken(tok), 0);
  const valid =
    !!identity &&
    !!wallet &&
    (!needsStore || !!storeEntry) &&
    missing.length === 0 &&
    tokensNeeded.every((tok) => capOf(tok) > 0 && !above(tok));

  function doInstall() {
    if (!valid || !identity || !wallet) return;
    setState("waiting");
    const record: InstallRecord = {
      id: view.id,
      owner: view.owner,
      name: m.name,
      icon: m.icon,
      version: m.version,
      hash: view.hash,
      url: view.url,
      identity: identity.address,
      ...(storeEntry ? { store: storeEntry.name } : {}),
      storage: m.storage ?? {},
      services: m.services ?? [],
      caps: allCaps,
      enabled: true,
      installedAt: new Date().toISOString(),
    };
    const commit = async () => {
      if (!(await ensurePayerCap([settings.defaultMemory]))) {
        setState("idle");
        return;
      }
      try {
        await installExtension(record);
        // the store's namespace is the extension's from here (issue #21); a bind that fails is tried again by its tab
        if (record.store) await storeBinding.bind(record);
        toast.success(t("ext.installedToast", { name: m.name }));
        onClose();
        navigate(`/extensions/${encodeURIComponent(view.id)}`);
      } catch (e) {
        setState("idle");
        toast.error(errorText(e));
      }
    };
    if (tokensNeeded.length === 0) {
      void commit();
      return;
    }
    request({
      title: Y("wallet.req.install", { name: m.name }),
      amount: totalWp,
      tokens: Object.entries(allCaps).map(([symbol, amount]) => ({ symbol, amount })),
      lines: [
        ...approveNeeded.map((tok) => lines.approve(tok)),
        lines.authorize(
          Y("wallet.plain.install", { name: m.name, caps: capsText(allCaps) }),
          identity.address,
          allCaps,
        ),
      ],
      onDone: (r) => {
        if (r !== "signed") {
          setState("rejected");
          return;
        }
        void commit();
      },
    });
  }

  const perm = (text: string, warn = false) => (
    <li key={text} className="flex items-start gap-2 text-sm">
      {warn ? (
        <TriangleAlertIcon className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
      ) : (
        <CheckIcon className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
      )}
      <span>{text}</span>
    </li>
  );
  const warning = (text: ReactNode, key: string) => (
    <div
      key={key}
      className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-200"
    >
      <TriangleAlertIcon className="size-4 shrink-0" />
      <span>{text}</span>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !busy && !o && onClose()}>
      <DialogContent
        className="max-h-[90svh] overflow-y-auto sm:max-w-xl"
        onInteractOutside={(e) => busy && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {t("install.title", { name: m.name })} <span className="font-mono text-xs font-normal">v{m.version}</span>
          </DialogTitle>
          <DialogDescription>
            {t("install.description", { id: view.id, identity: identity ? short(identity.address) : "…" })}
          </DialogDescription>
        </DialogHeader>
        <fieldset disabled={busy} className="flex min-w-0 flex-col gap-5">
          <p className="text-sm">{m.description}</p>
          <div className="grid gap-2">
            <Label>{t("install.declares")}</Label>
            <ul className="flex flex-col gap-1.5">
              {m.storage?.config &&
                perm(
                  t("install.perm.config", {
                    quota: m.storage.config,
                    token: memoryEntry?.token ?? "",
                    memory: memoryEntry?.name ?? "",
                  }),
                )}
              {m.storage?.store && perm(t("install.perm.store", { quota: m.storage.store }))}
              {(m.services?.length ?? 0) > 0 &&
                perm(t("install.perm.services", { names: (m.services ?? []).join(", ") }))}
              {m.permissions?.wallet?.includes("authorize") && perm(t("install.perm.wallet"), true)}
              {(m.permissions?.read?.length ?? 0) > 0 &&
                perm(t("install.perm.read", { what: m.permissions.read.join(", ") }))}
              {m.permissions?.notify && perm(t("install.perm.notify"))}
              {!m.storage?.config && !m.storage?.store && !(m.services?.length ?? 0) && perm(t("ext.needs.none"))}
            </ul>
          </div>
          {needsStore && (
            <div className="grid gap-2">
              <Label htmlFor="install-store">{t("install.store")}</Label>
              {stores.length > 0 ? (
                <Select value={storeName} onValueChange={setStoreName}>
                  <SelectTrigger id="install-store" className="w-full sm:max-w-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map((s) => (
                      <SelectItem key={s.name} value={s.name}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                warning(t("install.noStore"), "store")
              )}
            </div>
          )}
          {missing.map((name) => warning(t("install.missingService", { name }), name))}
          {!identity && warning(t("install.noIdentity"), "identity")}
          {tokensNeeded.length > 0 && (
            <div className="grid gap-2">
              <Label>{t("install.caps")}</Label>
              <div className="flex flex-col gap-2">
                {tokensNeeded.map((token) => (
                  <div
                    key={token}
                    className="grid gap-1 rounded-lg border p-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end"
                  >
                    <div className="min-w-0 text-sm">
                      <div className="font-medium">{owners.get(token)?.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {t("install.hold", { n: balanceOf(token), token })}
                      </div>
                    </div>
                    <div className="grid gap-1 sm:w-44">
                      <Label htmlFor={`install-cap-${token}`} className="text-xs font-normal text-muted-foreground">
                        {t("install.cap", { token })}
                      </Label>
                      <Input
                        id={`install-cap-${token}`}
                        type="number"
                        min={1}
                        step={1}
                        className="w-28"
                        value={capOf(token)}
                        onChange={(e) =>
                          setCaps((c) => ({ ...c, [token]: Math.max(0, Math.floor(Number(e.target.value) || 0)) }))
                        }
                      />
                      {above(token) && (
                        <div className="text-[11px] text-amber-700 dark:text-amber-400">
                          {t("install.above", { token })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </fieldset>
        <DialogFooter className="flex-col sm:items-center sm:justify-end">
          {tokensNeeded.length > 0 && (
            <div className="min-w-0 text-xs text-muted-foreground sm:mr-auto">
              {t("install.auths", { n: tokensNeeded.length, approves: approveNeeded.length })}
            </div>
          )}
          <div className="flex flex-col gap-2 sm:items-end">
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button variant="outline" onClick={onClose} disabled={busy}>
                {t("install.cancel")}
              </Button>
              <Button onClick={doInstall} disabled={!valid || busy}>
                {busy && <Loader2Icon className="animate-spin" />}
                {t("install.button")}
              </Button>
            </div>
            {busy && <Waiting n={approveNeeded.length + 1} className="text-xs" />}
            {state === "rejected" && <Rejected className="text-xs" />}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The installed extensions (D69, D72): pinned version and hash, the state of the pinned copy, the caps, the actions. */
function InstalledCard({ views, loading }: { views: InstalledView[]; loading: boolean }) {
  const { installed, setExtensionEnabled, menuOrder, setMenuOrder, wallet } = useStore();
  const uninstall = useUninstall();
  const capsOf = useCapsOf();
  const t = useT();
  const [caps, setCaps] = useState<Record<string, CapRow[]>>({});
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    let on = true;
    void Promise.all(
      installed.map(async (x) => [x.id, await capsOf(x.id, x.identity).catch(() => [] as CapRow[])] as const),
    ).then((rows) => {
      if (on) setCaps(Object.fromEntries(rows));
    });
    return () => {
      on = false;
    };
  }, [installed, capsOf]);
  const ordered = orderedIds(installed, menuOrder);
  const move = (id: string, by: number) => {
    const i = ordered.indexOf(id);
    const j = i + by;
    if (i < 0 || j < 0 || j >= ordered.length) return;
    const next = [...ordered];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setMenuOrder(next);
  };
  const toggle = async (id: string, enabled: boolean) => {
    setBusy(id);
    try {
      await setExtensionEnabled(id, enabled);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  if (installed.length === 0) return <p className="text-sm text-muted-foreground">{t("ext.installed.empty")}</p>;
  return (
    <Table>
      <THead>
        <TR>
          <TH>{t("ext.col.extension")}</TH>
          <TH>{t("ext.col.version")}</TH>
          <TH>{t("ext.col.state")}</TH>
          <TH>{t("ext.col.caps")}</TH>
          <TH />
        </TR>
      </THead>
      <TBody>
        {ordered.map((id, i) => {
          const x = installed.find((r) => r.id === id)!;
          const v = views.find((w) => w.record.id === id);
          const mine = !!wallet && x.owner.toLowerCase() === wallet.address.toLowerCase();
          const state =
            loading || !v
              ? "…"
              : !v.manifest
                ? t("ext.state.changed")
                : v.unpublished
                  ? t("ext.state.unpublishedInstalled")
                  : v.newerHash
                    ? t("ext.state.update")
                    : t("ext.state.ok");
          const tone =
            !v || loading
              ? ""
              : !v.manifest
                ? "text-amber-700 dark:text-amber-400"
                : v.newerHash || v.unpublished
                  ? "text-amber-700 dark:text-amber-400"
                  : "text-emerald-700 dark:text-emerald-400";
          return (
            <TR key={id}>
              <TD>
                <div className="font-medium">{x.name}</div>
                <div className="font-mono text-xs text-muted-foreground">{x.id}</div>
                <div className="text-xs text-muted-foreground">
                  {t("tab.identity")} <span className="font-mono">{short(x.identity)}</span>
                  {x.store ? ` · ${t("tab.store")} ${x.store}` : ""}
                </div>
              </TD>
              <TD className="font-mono text-xs">
                {x.version}
                <div className="text-muted-foreground">{hash16(x.hash)}</div>
              </TD>
              <TD className={cn("text-xs", tone)}>
                {state}
                {!x.enabled && <div className="text-muted-foreground">{t("ext.disabledBadge")}</div>}
              </TD>
              <TD>
                <CapsLine rows={caps[id] ?? []} />
              </TD>
              <TD className="text-right">
                <div className="flex flex-wrap items-center justify-end gap-1.5">
                  <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Switch checked={x.enabled} disabled={busy === id} onCheckedChange={(on) => void toggle(id, on)} />
                    {t("ext.enabled")}
                  </label>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t("ext.order.up")}
                    disabled={i === 0}
                    onClick={() => move(id, -1)}
                  >
                    <ChevronUpIcon />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t("ext.order.down")}
                    disabled={i === ordered.length - 1}
                    onClick={() => move(id, 1)}
                  >
                    <ChevronDownIcon />
                  </Button>
                  <Button size="sm" onClick={() => navigate(`/extensions/${encodeURIComponent(id)}`)}>
                    {t("ext.open")}
                  </Button>
                  {mine && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/extensions/${encodeURIComponent(id)}/manage`)}
                    >
                      {t("ext.manage")}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => void uninstall(x)}>
                    {t("ext.uninstall")}
                  </Button>
                </div>
              </TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}

/** The ids in this browser's menu order, the rest as installed (D69). */
function orderedIds(installed: InstallRecord[], order: string[]): string[] {
  const ids = installed.map((x) => x.id);
  return [...order.filter((id) => ids.includes(id)), ...ids.filter((id) => !order.includes(id))];
}

/** The Extensions page (D69): the installed extensions, this wallet's own, the Directory, the Publish link. */
export function ExtensionsPage() {
  const { chain, wallet, config, installed, signedIn } = useStore();
  const t = useT();
  const [data, setData] = useState<{ mine: ExtensionView[]; directory: ExtensionView[]; error?: string } | null>(null);
  const [views, setViews] = useState<InstalledView[]>([]);
  const [busy, setBusy] = useState(false);
  const [installing, setInstalling] = useState<ExtensionView | null>(null);
  const installedKey = JSON.stringify(installed.map((x) => [x.id, x.hash, x.url]));
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [d, v] = await Promise.all([
        loadExtensions(chain, wallet?.address ?? null),
        loadInstalled(chain, installed),
      ]);
      setData(d);
      setViews(v);
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain, wallet, installedKey]);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <>
      <PageHeader title={t("ext.title")} description={t("ext.description")} />
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => navigate("/extensions/publish")} disabled={!wallet}>
            <UploadIcon />
            {t("ext.publish")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={busy}>
            {busy ? <Loader2Icon className="animate-spin" /> : <RefreshCwIcon />}
            {t("ext.refresh")}
          </Button>
        </div>
        {!config.extensionDirectory || data?.error ? (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {t("ext.noDirectory", { detail: data?.error ?? "extensionDirectory is not in the configuration" })}
          </p>
        ) : null}
        {signedIn && (
          <Card>
            <CardHeader>
              <CardTitle>{t("ext.installed.title")}</CardTitle>
              <CardDescription>{t("ext.installed.description")}</CardDescription>
            </CardHeader>
            <CardContent>
              <InstalledCard views={views} loading={busy} />
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader>
            <CardTitle>{t("ext.my.title")}</CardTitle>
            <CardDescription>{t("ext.my.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            <ExtensionTable
              rows={data?.mine ?? []}
              empty={t("ext.my.empty")}
              manage
              installed={installed}
              onInstall={setInstalling}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t("ext.directory.title")}</CardTitle>
            <CardDescription>{t("ext.directory.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            <ExtensionTable
              rows={data?.directory ?? []}
              empty={t("ext.directory.empty")}
              manage={false}
              installed={installed}
              onInstall={setInstalling}
            />
          </CardContent>
        </Card>
      </div>
      {installing && <InstallDialog key={installing.id} view={installing} onClose={() => setInstalling(null)} />}
    </>
  );
}

type Step = "idle" | "cap" | "upload" | "register" | "done";

function problemText(t: ReturnType<typeof useT>, p: PackageProblem, handle: string): string {
  return t(`pub.problem.${p.code}`, { path: p.path ?? "", handle, detail: p.detail ?? "" });
}

/** The Publish page (D71): the package read in the browser, the paid upload, the wallet's publish. */
export function ExtensionPublishPage() {
  const { wallet, chain, config, directory, myServices, signedIn, payerAuthorization } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const ensurePayerCap = useEnsurePayerCap();
  const t = useT();
  const host: ServiceEntry | undefined =
    directory.find((e) => e.name === `${config.provider}/cf`) ?? myServices.find((e) => e.kinds.includes("host"));
  const [handle, setHandle] = useState<{ name: string; addr4: string; exists: boolean } | null>(null);
  const [cap, setCap] = useState<{ token: string; cap: number; used: number } | null>(null);
  const [fileName, setFileName] = useState("");
  const [reading, setReading] = useState(false);
  const [pkg, setPkg] = useState<(PackageCheck & { zip: Uint8Array }) | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [current, setCurrent] = useState<string | null>(null);
  const [step, setStep] = useState<Step>("idle");
  const [upload, setUpload] = useState<{ url: string; hash: Hex; tx: Hex; actual: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const addr4 = wallet ? wallet.address.slice(2, 6).toLowerCase() : "";
  const walletHandle = handle?.exists ? `${handle.name}@${handle.addr4}` : null;

  useEffect(() => {
    if (!wallet) return;
    void chain
      .dirHandleOf(wallet.address)
      .then(setHandle)
      .catch(() => setHandle(null));
  }, [chain, wallet]);
  useEffect(() => {
    if (!host) return;
    void payerAuthorization(host.name)
      .then((a) => setCap(a ? { token: a.token, cap: a.cap, used: a.used } : null))
      .catch(() => setCap(null));
  }, [host, payerAuthorization, step]);

  const read = async (file: File) => {
    if (!wallet) return;
    setReading(true);
    setFileName(file.name);
    setUpload(null);
    setError(null);
    setStep("idle");
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const check = readPackageZip(bytes, walletHandle ? { handle: walletHandle } : {});
      const list: string[] = check.problems.map((p) =>
        problemText(t, p, walletHandle ?? `${handle?.name ?? "…"}@${addr4}`),
      );
      let currentVersion: string | null = null;
      if (check.manifest && check.problems.length === 0) {
        const [idHandle, name] = check.manifest.id.split("/");
        // a wallet without a handle yet: the id's handle must carry this wallet's addr4
        if (!walletHandle && idHandle?.split("@")[1] !== addr4)
          list.push(t("pub.problem.handle", { handle: `…@${addr4}` }));
        // the version must be new: not at the host already, and not the chain's current one
        try {
          const e = await chain.extEntry(wallet.address, name!);
          currentVersion = null;
          if (e.hash.toLowerCase() === check.hash?.toLowerCase())
            list.push(t("pub.problem.version", { version: check.manifest.version }));
          if (host) {
            const versions = await new HostPackageClient(host.origin, host.name, wallet.address).versions(
              check.manifest.id,
            );
            const same = versions.find((v) => v.state === "confirmed" && v.version === check.manifest!.version);
            if (same && !list.some((x) => x.includes(check.manifest!.version)))
              list.push(t("pub.problem.version", { version: check.manifest.version }));
            currentVersion =
              versions.find((v) => v.hash.toLowerCase() === e.hash.toLowerCase())?.version ?? e.hash.slice(0, 10);
          }
        } catch {
          /* no entry yet: a first publish */
        }
      }
      setPkg({ ...check, zip: bytes });
      setProblems(list);
      setCurrent(currentVersion);
    } finally {
      setReading(false);
    }
  };

  const ready = !!wallet && !!host && !!pkg?.manifest && !!pkg.hash && problems.length === 0 && step === "idle";

  const publish = async () => {
    if (!wallet || !host || !pkg?.manifest || !pkg.hash) return;
    const manifest = pkg.manifest;
    const hash = pkg.hash;
    const name = manifest.id.split("/")[1]!;
    setError(null);
    try {
      setStep("cap");
      if (!(await ensurePayerCap([host.name]))) {
        setStep("idle");
        return;
      }
      const root = rootOf(config.chainId, wallet.address);
      if (!root) throw new Error(t("wallet.payer.noRoot"));
      const payer = consoleIdentityOf(root);
      setStep("upload");
      const client = new HostPackageClient(host.origin, host.name, wallet.address);
      const { result, receipt } = await paidWrite({
        entry: host,
        wallet: wallet.address,
        payer,
        chainId: config.chainId,
        contractA: config.contractA,
        pending: () => client.uploadPending(manifest.id, pkg.zip, payer),
        settle: (calls, signed) => client.settleUpload(manifest.id, calls, signed, payer),
      });
      setUpload({ url: result.url, hash: result.hash, tx: result.tx, actual: receipt.actual });
      setStep("register");
      const signLines = [
        ...(walletHandle ? [] : [lines.claim(manifest.id.split("@")[0]!)]),
        lines.publish(manifest.id, manifest.version, name, result.url, result.hash),
      ];
      request({
        title: Y("wallet.req.publish", { id: manifest.id, version: manifest.version }),
        note: Y("wallet.req.publishNote"),
        lines: signLines,
        onDone: (r, states) => {
          if (r === "signed") {
            setStep("done");
            toast.success(t("pub.toast", { id: manifest.id, version: manifest.version }));
            navigate(`/extensions/${encodeURIComponent(manifest.id)}`);
          } else {
            setStep("idle");
            if (r === "failed") setError(states.find((l) => l.error)?.error ?? "");
          }
        },
      });
      void hash;
    } catch (e) {
      setStep("idle");
      setError(t("pub.error.upload", { detail: errorText(e) }));
    }
  };

  const stepRow = (key: Step, label: string) => {
    const order: Step[] = ["cap", "upload", "register", "done"];
    const i = order.indexOf(key);
    const at = order.indexOf(step);
    const state = step === "done" || at > i ? "done" : at === i ? "running" : "pending";
    return (
      <li key={key} className="flex items-center gap-2 text-sm">
        {state === "done" ? (
          <CheckIcon className="size-4 text-emerald-600" />
        ) : state === "running" ? (
          <Loader2Icon className="size-4 animate-spin" />
        ) : (
          <span className="inline-block size-4 rounded-full border" />
        )}
        <span className={cn(state === "pending" && "text-muted-foreground")}>{label}</span>
        {key === "upload" && upload && (
          <span className="font-mono text-xs text-muted-foreground">
            {t("pub.uploaded", { actual: upload.actual, token: host?.token ?? "", tx: short(upload.tx) })}
          </span>
        )}
      </li>
    );
  };

  return (
    <>
      <a href="#/extensions" className="text-sm text-muted-foreground hover:underline">
        {t("pub.back")}
      </a>
      <PageHeader title={t("pub.title")} description={t("pub.description")} />
      {!signedIn ? (
        <SignedOut what="signedOut.settings" />
      ) : (
        <div className="flex flex-col gap-4">
          <Card size="sm">
            <CardHeader>
              <CardTitle>{t("pub.prereq.title")}</CardTitle>
              <CardDescription>{t("pub.prereq.description")}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-1 text-sm">
              <div>
                <span className="inline-block w-28 text-muted-foreground">{t("pub.prereq.host")}</span>
                {host ? (
                  <span className="font-mono">{host.name}</span>
                ) : (
                  <span className="text-amber-700">{t("pub.prereq.noHost")}</span>
                )}
              </div>
              <div>
                <span className="inline-block w-28 text-muted-foreground">{t("pub.prereq.handle")}</span>
                <span className="font-mono">{walletHandle ?? t("pub.prereq.handleNone")}</span>
              </div>
              <div>
                <span className="inline-block w-28 text-muted-foreground">{t("pub.prereq.cap")}</span>
                <span>
                  {cap && cap.cap > 0
                    ? t("pub.prereq.capHas", { token: cap.token, used: cap.used, cap: cap.cap })
                    : t("pub.prereq.capNone", { token: host?.token ?? "tCloud" })}
                </span>
              </div>
            </CardContent>
          </Card>
          <Card size="sm">
            <CardHeader>
              <CardTitle>{t("pub.package.title")}</CardTitle>
              <CardDescription>{t("pub.package.description")}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  id="package-file"
                  type="file"
                  accept=".zip,application/zip"
                  className="max-w-sm"
                  aria-label={t("pub.package.choose")}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void read(f);
                  }}
                />
                {reading && <span className="text-xs text-muted-foreground">{t("pub.package.reading")}</span>}
                {fileName && !reading && <span className="font-mono text-xs">{fileName}</span>}
              </div>
              {pkg?.manifest && (
                <div className="grid gap-1 text-sm">
                  <div className="flex items-center gap-2">
                    <PackageIcon className="size-4" />
                    <span className="font-medium">{pkg.manifest.name}</span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {pkg.manifest.id} · {pkg.manifest.version} · api {pkg.manifest.api} · {pkg.manifest.entry}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">{pkg.manifest.description}</div>
                  <div className="text-xs text-muted-foreground">{needsOf(t, pkg.manifest).join(" · ")}</div>
                  <div className="font-mono text-xs">
                    {t("pub.package.hash")}: {pkg.hash}
                  </div>
                  <Table>
                    <THead>
                      <TR>
                        <TH>{t("pub.col.path")}</TH>
                        <TH className="text-right">{t("pub.col.size")}</TH>
                        <TH>{t("pub.col.sha256")}</TH>
                        <TH>{t("pub.col.declared")}</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {pkg.files.map((f) => (
                        <TR key={f.path}>
                          <TD className="font-mono text-xs">{f.path}</TD>
                          <TD className="text-right font-mono text-xs">{f.size}</TD>
                          <TD className="font-mono text-xs">{f.sha256.slice(0, 16)}…</TD>
                          <TD className="font-mono text-xs">
                            {f.declared === f.sha256 ? (
                              <CheckIcon className="size-4 text-emerald-600" />
                            ) : (
                              (f.declared ?? "—")
                            )}
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                </div>
              )}
              {problems.length > 0 && (
                <ul className="list-disc pl-5 text-sm text-destructive">
                  {problems.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              )}
              {pkg?.manifest && problems.length === 0 && (
                <p className="text-sm">
                  {current
                    ? t("pub.package.newVersion", { id: pkg.manifest.id, version: pkg.manifest.version, current })
                    : t("pub.package.ready", { id: pkg.manifest.id, version: pkg.manifest.version })}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <Button disabled={!ready} onClick={() => void publish()}>
                  {step !== "idle" && step !== "done" ? <Loader2Icon className="animate-spin" /> : <UploadIcon />}
                  {t("pub.button")}
                </Button>
                {error && <span className="text-sm text-destructive">{error}</span>}
              </div>
              {step !== "idle" && (
                <ul className="flex flex-col gap-1">
                  {stepRow("cap", t("pub.step.cap"))}
                  {stepRow("upload", t("pub.step.upload", { host: host?.name ?? "" }))}
                  {stepRow("register", t("pub.step.register"))}
                  {stepRow("done", t("pub.step.done"))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

/** The management page of one extension (D69, D71): the directory entry, the versions at the host, Unpublish. */
export function ExtensionManagePage({ id }: { id: string }) {
  const { chain, wallet, config, directory, myServices } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const t = useT();
  const host: ServiceEntry | undefined =
    directory.find((e) => e.name === `${config.provider}/cf`) ?? myServices.find((e) => e.kinds.includes("host"));
  const [view, setView] = useState<ExtensionView | null | undefined>(undefined);
  const [versions, setVersions] = useState<PackageVersion[]>([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const v = await loadExtension(chain, id, wallet?.address ?? null);
    setView(v);
    if (v && host && wallet)
      setVersions(await new HostPackageClient(host.origin, host.name, wallet.address).versions(id));
  }, [chain, id, wallet, host]);
  useEffect(() => {
    void load();
  }, [load]);
  const unpublish = () => {
    if (!view) return;
    setBusy(true);
    request({
      title: Y("wallet.req.unpublish", { id }),
      lines: [lines.unpublish(id, view.name)],
      onDone: (r) => {
        setBusy(false);
        if (r === "signed") {
          toast.success(t("extman.unpublished", { id }));
          void load();
        }
      },
    });
  };
  return (
    <>
      <a href="#/extensions" className="text-sm text-muted-foreground hover:underline">
        {t("pub.back")}
      </a>
      <PageHeader title={t("extman.title", { id })} description={view?.manifest?.description ?? ""} />
      {view === undefined ? (
        <Loader2Icon className="size-5 animate-spin" />
      ) : view === null ? (
        <p className="text-sm text-muted-foreground">{t("extman.notFound", { id })}</p>
      ) : !view.mine ? (
        <p className="text-sm text-muted-foreground">{t("extman.notMine")}</p>
      ) : (
        <div className="flex flex-col gap-4">
          <Card size="sm">
            <CardHeader>
              <CardTitle>{t("extman.entry.title")}</CardTitle>
              <CardDescription>{t("extman.entry.description")}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-1 text-sm">
              <div>
                <span className="inline-block w-24 text-muted-foreground">{t("extman.entry.url")}</span>
                <a
                  className="font-mono text-xs underline"
                  href={`${view.url}manifest.json`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {view.url}
                </a>
              </div>
              <div>
                <span className="inline-block w-24 text-muted-foreground">{t("extman.entry.hash")}</span>
                <span className="font-mono text-xs">{view.hash}</span>
              </div>
              <div>
                <span className="inline-block w-24 text-muted-foreground">{t("extman.entry.updated")}</span>
                <span className="font-mono text-xs">
                  {new Date(view.updatedAt * 1000).toISOString().slice(0, 16).replace("T", " ")}
                </span>
              </div>
              <div>
                <span className="inline-block w-24 text-muted-foreground">{t("extman.entry.state")}</span>
                <StateBadge v={view} />
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => navigate("/extensions/publish")}>
                  <UploadIcon />
                  {t("extman.publishAgain")}
                </Button>
                {view.active && (
                  <Button variant="outline" size="sm" disabled={busy} onClick={unpublish}>
                    {busy ? <Loader2Icon className="animate-spin" /> : null}
                    {t("extman.unpublish")}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
          <Card size="sm">
            <CardHeader>
              <CardTitle>{t("extman.versions.title")}</CardTitle>
              <CardDescription>{t("extman.versions.description")}</CardDescription>
            </CardHeader>
            <CardContent>
              {versions.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("extman.versions.empty")}</p>
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>{t("ext.col.version")}</TH>
                      <TH>{t("ext.col.hash")}</TH>
                      <TH>{t("ext.col.state")}</TH>
                      <TH />
                    </TR>
                  </THead>
                  <TBody>
                    {versions.map((v) => (
                      <TR key={v.hash}>
                        <TD className="font-mono text-xs">{v.version}</TD>
                        <TD className="font-mono text-xs">{hash16(v.hash)}</TD>
                        <TD className="text-xs">
                          {v.state}
                          {v.hash.toLowerCase() === view.hash.toLowerCase() && (
                            <span className="ml-2 text-emerald-700 dark:text-emerald-400">
                              {t("extman.versions.current")}
                            </span>
                          )}
                        </TD>
                        <TD className="text-right">
                          <a
                            className="text-xs underline"
                            href={`${v.url}manifest.json`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            manifest.json
                          </a>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
